import TermsAndConditions from '../Components/Login/ui/TermsAndConditions';
import {
  LOGIN_SET_IS_LOADING,
  LOGIN_SET_ORGANIZATIONS,
  LOGIN_SET_USERNAME_PASSWORD,
  LOGIN_SET_MODAL_OPEN,
  LOGIN_SET_TERMS,
  LOGIN_SET_LOGIN_MODE,
  LOGIN_ERROR,
  LOGIN_SET_REDIRECT_URL,
  LOGIN_SET_MAGIC_LINK_MODEL,
  LOGIN_SET_SHOW_MARKETING_OPT_IN,
  LOGIN_SET_HAS_ACCEPTED_TERMS,
  LoginModes,
} from '../constants';
import { post } from '../Services/http';
import { translate } from '../Services/translation';

export const performLogin = (username, password, recaptchaValue) => (dispatch) => {
  dispatch(setLoginFormLoading(true));
  const redirectUrl = new URLSearchParams(location.search.toLowerCase()).get(
    'redirecturl'
  );

  post(`/api/login${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''}`, {
    username,
    password,
    redirectUrl,
    recaptchaValue,
  })
    .then((res) => res.json())
    .then((response) => dispatch(handleLoginResponse(response)))
    .catch((err) => dispatch(handleErrorResponse(err)));
};

export const selectOrganization = (selectedOrganizationId) => (
  dispatch,
  getState
) => {
  dispatch(setLoginFormLoading(true));
  const redirectUrl = getState().login.redirectUrl;
  post(
    `/api/login/organizations/${selectedOrganizationId}${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''
    }`,
    {}
  )
    .then((res) => res.json())
    .then((response) => {
      dispatch(onError(response.error));
      if (response.success) {
        onSuccessfulLogin(response);
      } else {
        dispatch(setLoginFormLoading(false));
      }
    })
    .catch((err) => dispatch(handleErrorResponse(err)));
};

export const selectOrganizationPage = (selectedOrganizationId, redirectUrl) => (
  dispatch,
  getState
) => {
  dispatch(setLoginFormLoading(true));
  post(
    `/api/login/organizations/${selectedOrganizationId}?redirectUrl=${redirectUrl}`,
    {}
  )
    .then((res) => res.json())
    .then((response) => {
      dispatch(onError(response.error));
      if (response.success) {
        onSuccessfulLogin(response);
      } else {
        dispatch(setLoginFormLoading(false));
      }
    })
    .catch((err) => dispatch(handleErrorResponse(err)));
};

export const submitChangePassword = (
  username,
  oldPassword,
  newPassword,
  passwordConfirmation,
  acceptMarketing = false
) => (dispatch, getState) => {
  if (newPassword !== passwordConfirmation) {
    dispatch(onError(translate('login.password-mismatch')));
    return;
  }

  const { redirectUrl, magicLinkModel } = getState().login;
  dispatch(setLoginFormLoading(true));
  post(
    `/api/login/changepassword${redirectUrl ? `?redirectUrl=${redirectUrl}` : ''
    }`,
    {
      username,
      oldPassword,
      newPassword,
      magicLinkModel,
      acceptMarketing,
    }
  )
    .then((res) => res.json())
    .then((response) => {
      if (response.success) {
        // Activation/set-password flow: allow the communication-preferences step next.
        dispatch(handleLoginResponse(response, true));
      } else {
        dispatch(onError(response.error));
        dispatch(setLoginFormLoading(false));
      }
    })
    .catch((err) => dispatch(handleErrorResponse(err)));
};

export const submitForgotPassword = (username) => (dispatch) => {
  return post('/api/login/forgotpassword', { username })
    .then((res) => res.json())
    .then((response) => {
      dispatch(onError(response.error));
      return response.success;
    })
    .catch((err) => dispatch(handleErrorResponse(err)))
    .finally(() => dispatch(setLoginFormLoading(false)));
};

export const setOrganizations = (organizations) => ({
  type: LOGIN_SET_ORGANIZATIONS,
  payload: {
    organizations,
  },
});

export const setUsername = (username) => ({
  type: LOGIN_SET_USERNAME_PASSWORD,
  payload: {
    username,
  },
});

export const setPassword = (password) => ({
  type: LOGIN_SET_USERNAME_PASSWORD,
  payload: {
    password,
  },
});

export const setLoginModalOpen = (modalOpen) => ({
  type: LOGIN_SET_MODAL_OPEN,
  payload: {
    modalOpen,
  },
});

export const setLoginMode = (loginMode) => ({
  type: LOGIN_SET_LOGIN_MODE,
  payload: {
    loginMode,
    error: ''
  },
});

export const setTerms = (terms) => ({
  type: LOGIN_SET_TERMS,
  payload: {
    terms,
  },
});

export const setShowMarketingOptIn = (showMarketingOptIn) => ({
  type: LOGIN_SET_SHOW_MARKETING_OPT_IN,
  payload: {
    showMarketingOptIn,
  },
});

export const setHasAcceptedTerms = (hasAcceptedTerms) => ({
  type: LOGIN_SET_HAS_ACCEPTED_TERMS,
  payload: {
    hasAcceptedTerms,
  },
});

// Step 2 submit: record the marketing decision, then continue to terms / select org / done.
export const submitMarketingConsent = (acceptMarketing) => (dispatch, getState) => {
  dispatch(setLoginFormLoading(true));
  post('/api/person/marketingconsent', { acceptMarketing })
    .then(() => {
      const { hasAcceptedTerms, organizations, redirectUrl } = getState().login;
      if (!hasAcceptedTerms) {
        dispatch(setLoginMode(LoginModes.Terms));
        dispatch(setLoginFormLoading(false));
      } else if (organizations && organizations.length > 1) {
        dispatch(setLoginMode(LoginModes.SelectOrganization));
        dispatch(setLoginFormLoading(false));
      } else {
        // Navigate to a clean URL (no magic link) so the activation link isn't re-processed.
        window.location.href = redirectUrl || '/';
      }
    })
    .catch((error) => {
      console.error('Error saving marketing preference:', error);
      dispatch(onError(translate('general.error')));
      dispatch(setLoginFormLoading(false));
    });
};

export const handleTermsAndConditionsAccepted = (fields) => {
  return (dispatch, getState) => {
    dispatch(setLoginFormLoading(true));

    post('/api/login/terms', fields)
      // The endpoint returns a plain string body ("SelectOrganization" or ""), not JSON.
      .then((res) => res.text())
      .then((mode) => {
        if (mode === LoginModes.SelectOrganization) {
          dispatch(setLoginMode(LoginModes.SelectOrganization));
          dispatch(setLoginFormLoading(false));
        } else {
          // Navigate to a clean URL (without the magic link) so the activation link is not
          // re-processed on load — that re-processing is what caused the set-password loop.
          const { redirectUrl } = getState().login;
          window.location.href = redirectUrl || '/';
        }
      })
      .catch((error) => {
        console.error('Error accepting terms:', error);
        dispatch(setLoginFormLoading(false));
      });
  };
};

export const signOut = () => {
  post('/api/login/logout', null).then(() => (location.href = '/'));
};

export const submitMagicLink = (magicLinkModel) => (dispatch) => {
  dispatch(setLoginFormLoading(true));
  return post('/api/login/magiclink', magicLinkModel)
    .then((res) => res.json())
    .then((response) => {
      dispatch(handleLoginResponse(response));
    })
    .catch((err) => {
      dispatch(setLoginMode(LoginModes.Login));
      dispatch(setMagicLinkModel(undefined));
      return dispatch(handleErrorResponse(err));
    })
    .finally(() => dispatch(setLoginFormLoading(false)));
};

const setMagicLinkModel = (magicLinkModel) => ({
  type: LOGIN_SET_MAGIC_LINK_MODEL,
  payload: {
    magicLinkModel,
  },
});

const handleErrorResponse = (err) => (dispatch) => {
  // Fetch errors from the http service carry the original Response on err.response.
  if (err && err.response && typeof err.response.json === 'function') {
    err.response
      .json()
      .then((data) => dispatch(onError((data && data.error) || translate('general.error'))))
      .catch(() => dispatch(onError(translate('general.error'))));
  } else {
    // Not a fetch error (e.g. a runtime error in a response handler). Log the real cause instead
    // of throwing a secondary "cannot read 'json'" error, and never leave the form spinning.
    console.error('Login request failed:', err);
    dispatch(onError(translate('general.error')));
  }
  dispatch(setLoginFormLoading(false));
};

const onError = (error) => ({
  type: LOGIN_ERROR,
  payload: {
    error,
  },
});

const setLoginFormLoading = (isLoading) => ({
  type: LOGIN_SET_IS_LOADING,
  payload: {
    isLoading,
  },
});

// Decide the next onboarding step after a successful auth / password response.
// Order: Communication preferences (step 2) -> Terms (step 3) -> Select organization -> done.
const proceedToNextStep = (response, dispatch, allowMarketingStep) => {
  if (response.terms !== undefined) {
    dispatch(setTerms(response.terms));
  }
  dispatch(setHasAcceptedTerms(!!response.hasAcceptedTermsAndConditions));
  if (response.organizations) {
    dispatch(setOrganizations(response.organizations));
  }

  // Step 2: communication preferences (only in the activation / set-password flow, not on
  // a normal login).
  if (allowMarketingStep && response.showMarketingOptIn) {
    dispatch(setLoginMode(LoginModes.Communication));
    dispatch(setLoginFormLoading(false));
    return;
  }

  // Step 3: terms & conditions.
  if (!response.hasAcceptedTermsAndConditions) {
    dispatch(setLoginMode(LoginModes.Terms));
    dispatch(setLoginFormLoading(false));
    return;
  }

  // Multi-organization users pick an organization; everyone else is done.
  if (response.organizations && response.organizations.length > 1) {
    dispatch(setLoginMode(LoginModes.SelectOrganization));
    dispatch(setLoginFormLoading(false));
    return;
  }

  onSuccessfulLogin(response);
};

const handleLoginResponse = (response, allowMarketingStep = false) => (dispatch) => {
  dispatch(onError(response.error));

  if (!response.success) {
    dispatch(setLoginFormLoading(false));
    return;
  }

  if (response.requestToken) {
    localStorage.setItem('requestVerificationToken', response.requestToken);
  }

  dispatch(setRedirectUrl(response.redirectUrl));

  // Step 1: set a new password. The follow-up steps are decided from the change-password response.
  if (response.mustChangePassword) {
    dispatch(setLoginMode(LoginModes.ChangePassword));
    dispatch(setLoginFormLoading(false));
    return;
  }

  proceedToNextStep(response, dispatch, allowMarketingStep);
};

const setRedirectUrl = (redirectUrl) => ({
  type: LOGIN_SET_REDIRECT_URL,
  payload: {
    redirectUrl,
  },
});

const onSuccessfulLogin = (response) => {
  if (response.redirectUrl) {
    location.href = response.redirectUrl;
  } else {
    location.reload();
  }
};
